import React, { useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { Cancel, CheckCircle, Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';

import Button from 'components/Button';
import Text from 'components/Text';
import { useThemeContext } from 'global/ThemeContext/ThemeContext';
import { useUser } from 'global/UserContext';
import { useToast } from 'hooks/useToast';
import { apiOrchestrator } from 'services/apiService';
import theme from 'styles/theme';

const passwordRequirements = [
  { test: /[A-Z]/, message: 'Contém pelo menos 1 letra maiúscula' },
  { test: /[a-z]/, message: 'Contém pelo menos 1 letra minúscula' },
  { test: /\d/, message: 'Contém pelo menos 1 número' },
  { test: /[\W_]/, message: 'Contém pelo menos 1 caractere especial' },
  { test: /.{8,}/, message: 'Contém no mínimo 8 caracteres' },
];

const passwordSchema = yup.object().shape({
  newPassword: yup
    .string()
    .min(8, 'A senha deve ter no mínimo 8 caracteres')
    .matches(/[A-Z]/, 'A senha deve conter pelo menos 1 letra maiúscula')
    .matches(/[a-z]/, 'A senha deve conter pelo menos 1 letra minúscula')
    .matches(/\d/, 'A senha deve conter pelo menos 1 número')
    .matches(/[\W_]/, 'A senha deve conter pelo menos 1 caractere especial')
    .required('Senha é obrigatória'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('newPassword')], 'As senhas devem coincidir')
    .required('Confirmação de senha é obrigatória'),
});

interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
  session: string;
  email: string;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  open,
  onClose,
  session,
  email,
}) => {
  const themeContext = useThemeContext();
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    resolver: yupResolver(passwordSchema),
  });
  const { toastSuccess, toastError } = useToast();
  const { initializeUserContext } = useUser();
  const [loading, setLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  const toggleShowNewPassword = () => setShowNewPassword(!showNewPassword);
  const toggleShowConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);

  const confirmPassword = watch('confirmPassword');

  const checkPasswordRequirements = (password: string) => {
    return passwordRequirements.map(req => ({
      ...req,
      isValid: req.test.test(password),
    }));
  };

  const isFormValid = () => {
    const passwordReqValid = checkPasswordRequirements(newPassword).every(req => req.isValid);
    const passwordsMatch = confirmPassword === newPassword;

    return passwordReqValid && passwordsMatch && Object.keys(errors).length === 0;
  };

  const onSubmit = async (data: { newPassword: string }) => {
    setLoading(true);
    try {
      const response = await apiOrchestrator.post('/auth/respond_to_new_password/', {
        email,
        new_password: data.newPassword,
        session: session,
      });

      if (response.data.status === 'success') {
        const { access_token, user_id } = response.data;

        // Usar initializeUserContext para fazer toda a inicialização e redirecionamento
        await initializeUserContext(user_id, access_token);

        toastSuccess('Senha alterada com sucesso!');
        onClose();
      } else {
        toastError('Erro ao redefinir senha.');
      }
    } catch (error) {
      console.error('[ChangePasswordModal] Erro ao redefinir senha:', error);
      toastError('Erro ao enviar nova senha. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          backgroundColor: 'background.default',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: 20,
          fontWeight: 700,
          textAlign: 'center',
        }}
      >
        REDEFINIR SENHA
      </DialogTitle>
      <Divider />

      <Box sx={{ padding: 2 }}>
        <Typography paddingBottom={2}>
          É necessário redefinir a senha para continuar com o Login.
        </Typography>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="newPassword"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Nova Senha"
                type={showNewPassword ? 'text' : 'password'}
                fullWidth
                error={!!errors.newPassword}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={toggleShowNewPassword}
                        edge="end"
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                style={{ marginBottom: '1rem' }}
                onChange={e => {
                  field.onChange(e);
                  setNewPassword(e.target.value);
                }}
              />
            )}
          />

          <Box sx={{ textAlign: 'left', marginBottom: '1rem' }}>
            <Text size="small">Requisitos de senha:</Text>
            {checkPasswordRequirements(newPassword).map((req, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {req.isValid ? (
                  <CheckCircle sx={{ fontSize: 12, color: themeContext.success }} />
                ) : (
                  <Cancel sx={{ fontSize: 12, color: themeContext.error }} />
                )}
                <Text size="small">{req.message}</Text>
              </Box>
            ))}
          </Box>

          <Controller
            name="confirmPassword"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Confirme a Nova Senha"
                type={showConfirmPassword ? 'text' : 'password'}
                fullWidth
                error={!!errors.confirmPassword}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={toggleShowConfirmPassword}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                style={{ marginBottom: '1rem' }}
              />
            )}
          />

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '1rem',
            }}
          >
            {confirmPassword === newPassword ? (
              <CheckCircle sx={{ fontSize: 12, color: themeContext.success }} />
            ) : (
              <Cancel sx={{ fontSize: 12, color: themeContext.error }} />
            )}
            <Text size="small">
              {confirmPassword === newPassword ? 'As senhas coincidem' : 'As senhas não coincidem'}
            </Text>
          </Box>

          <Button type="submit" variant="primary" fullWidth disabled={loading || !isFormValid()}>
            {loading ? (
              <CircularProgress size="1rem" sx={{ color: theme.COLORS.BLACK }} />
            ) : (
              'Redefinir Senha'
            )}
          </Button>
        </form>
      </Box>
    </Dialog>
  );
};

export default ChangePasswordModal;
